import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import OrderPaymentStatus from './OrderPaymentStatus';

const money = value => Number(value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

export default function OutletSalesOrderDetails({ orderId, token, onClose }) {
  const dialogRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog.open) dialog.showModal();
    return () => { if (dialog.open) dialog.close(); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setOrder(null);
    setError('');
    axios.get(`${API_BASE_URL}/orders/pos/orders/details/${encodeURIComponent(orderId)}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid order details response.');
      setOrder(data);
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.response?.data?.message || err.message || 'Unable to load order details.');
    });
    return () => controller.abort();
  }, [orderId, token, revision]);

  const items = order?.orderItems?.length ? order.orderItems : order?.items || [];
  const remarks = Array.isArray(order?.remarks)
    ? order.remarks.map(entry => typeof entry === 'string' ? entry : entry?.message).filter(Boolean).join('\n')
    : typeof order?.remarks === 'string' ? order.remarks : '';
  return createPortal(<dialog ref={dialogRef} aria-labelledby="outlet-order-title" onCancel={onClose}
    className="m-auto max-h-[90vh] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto rounded-xl bg-white p-0 shadow-xl backdrop:bg-black/50">
    <div className="flex items-center justify-between gap-3 border-b p-4">
      <h2 id="outlet-order-title" className="text-lg font-bold">Order Details</h2>
      <button type="button" autoFocus onClick={onClose} className="rounded-lg border px-3 py-2 font-semibold">Close</button>
    </div>
    <div className="space-y-4 p-4">
      {error ? <div role="alert"><p className="text-red-700">{error}</p><button type="button" onClick={() => setRevision(value => value + 1)} className="mt-2 rounded border px-3 py-2">Retry</button></div>
        : !order ? <p role="status">Loading order details…</p> : <>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            {[
              ['Order ID', order.MK_order_id || order.orderId || order._id],
              ['Date', order.createdAt ? new Date(order.createdAt).toLocaleString('en-IN') : '-'],
              ['Outlet', order.posLocation],
              ['Cashier', order.posUserName || order.cashierName || order.cashier],
              ['Customer', order.customerName || order.user?.name],
              ['Phone', order.phoneNo || order.user?.phoneNo],
            ].map(([label, value]) => <div key={label}><dt className="text-gray-500">{label}</dt><dd className="font-semibold">{value || '-'}</dd></div>)}
          </dl>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <caption className="p-3 text-left font-bold">Order Items</caption>
              <thead className="bg-slate-100"><tr>{['Item', 'Pack', 'Qty', 'Unit Price', 'Amount'].map(label => <th key={label} className="p-2 text-left">{label}</th>)}</tr></thead>
              <tbody>{items.map((item, index) => {
                const qty = Number(item.qty ?? item.quantityOrdered ?? 1);
                const price = Number(item.price ?? item.pricePerQty ?? item.dprice ?? 0);
                return <tr key={index} className="border-t">
                  <td className="p-2">{item.name || item.item || '-'}{item.brand && <div className="text-xs text-gray-500">{item.brand}</div>}</td>
                  <td className="p-2">{item.weight || [item.quantity, item.units].filter(Boolean).join(' ') || '-'}</td>
                  <td className="p-2">{qty}</td><td className="p-2">{money(price)}</td><td className="p-2">{money(qty * price)}</td>
                </tr>;
              })}{!items.length && <tr><td colSpan={5} className="p-3">No item details available.</td></tr>}</tbody>
            </table>
          </div>
          {Number(order.discountAmount) > 0 && <p className="text-sm">Discount: <strong>{money(order.discountAmount)}</strong></p>}
          <OrderPaymentStatus order={order} />
          {remarks && <p className="whitespace-pre-wrap text-sm"><strong>Remarks: </strong>{remarks}</p>}
        </>}
    </div>
  </dialog>, document.body);
}
